import styled from 'styled-components';
import { Navigation, Pagination, Virtual } from 'swiper';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import Image from 'next/image';
import { cloudFlareLoader } from 'utils/images';
import { mediaMMdMin } from 'helpers/breakpoints';
import { useFeatureFlagQueryByName } from 'components/layouts/hooks';
import { ComponentProps } from 'react';

interface Photo {
  standard?: { url?: URL };
  original?: { url?: URL };
}

// TODO possibly make it as common component - also similar in PDP3
interface GalleryProps {
  photos: Photo[];
  cardIndex: number;
  onChange: ComponentProps<typeof StyledSwiper>['onSlideChange'];
}
export default function Gallery({ photos, cardIndex, onChange }: GalleryProps) {
  const { flag: isOGImageUrlEnabled, isFetched } = useFeatureFlagQueryByName('use_original_boat_images');
  // Not all endpoints that pass info to this components include the original images.
  const shouldUseOriginal = isOGImageUrlEnabled && !!photos[0]?.original;
  const photoUrls = photos.map((photo) => (shouldUseOriginal ? photo?.original?.url : photo?.standard?.url));

  return (
    <StyledSwiper
      data-testid="search-boat-card-photo-swiper"
      spaceBetween={0}
      pagination={{ type: 'fraction' }}
      modules={[Pagination, Navigation, Virtual]}
      virtual
      navigation={{ prevEl: '.prev', nextEl: '.next' }}
      onSlideChange={onChange}
    >
      {photoUrls.map((url, i) => (
        <SwiperSlide key={String(url)}>
          {/* TODO possibly add loading same as in pdp3 NextImageWithLoading */}
          {isFetched && (
            <Image
              src={new URL(url)?.pathname.slice(1)}
              loader={cloudFlareLoader}
              priority={i === 0 && cardIndex < 3}
              fill
              alt=""
              sizes="(max-width: 2559px) calc(calc(100vw - 32vw) / 3 - 24px - 42px), (max-width: 1919px) calc(calc(100vw - 32vw) / 2 - 24px - 42px), (max-width: 1152px) calc(100vw / 3 - 42px - 42px), (max-width: 959px) calc(100vw / 2 - 42px - 42px), (max-width: 769px) calc(100vw - 42px - 42px), calc(100vw - 22px - 22px)"
              style={{
                objectFit: 'cover',
              }}
            />
          )}
        </SwiperSlide>
      ))}
      <StyledArrowPrevIcon className="prev" data-testid="search-boat-card-photo-swiper-prev" />
      <StyledArrowNextIcon className="next" data-testid="search-boat-card-photo-swiper-next" />
    </StyledSwiper>
  );
}

const StyledSwiper = styled(Swiper)`
  height: 256px;

  .swiper-slide {
    width: 100% !important;
  }

  .swiper-pagination-fraction {
    background: #17233ca1;
    border-radius: 34px;

    color: #fff;
    font-size: 10px;
    font-weight: 600;
    line-height: 19px;
    width: unset;
    left: 18px;
    bottom: 14px;
    padding: 0 9px;
  }

  &:hover {
    .next,
    .prev {
      &:not(.swiper-button-disabled) {
        opacity: 1;
      }
      &.swiper-button-disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
`;

export const ArrowNextIcon = (props) => (
  <svg width={29} height={29} fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <circle cx={14.5} cy={14.5} r={14.5} fill="#F5F7F9" />
    <path
      d="m18.125 14.5-4.66-4.406a.478.478 0 0 0-.645 0 .417.417 0 0 0 0 .61l4.015 3.796-4.015 3.796a.417.417 0 0 0 0 .61.478.478 0 0 0 .645 0l4.66-4.406Z"
      fill="#0751C2"
    />
    <path
      d="m18.125 14.5.343.363a.5.5 0 0 0 0-.726l-.343.363Zm-4.66-4.406-.344.363.344-.363Zm-.645.61-.344.363.344-.363Zm4.015 3.796.344.363.384-.363-.384-.363-.344.363Zm-4.015 3.796-.344-.363.344.363Zm.645.61.343.364-.343-.364Zm5.003-4.77-4.66-4.405-.687.726 4.66 4.406.688-.726Zm-4.66-4.405a.978.978 0 0 0-1.332 0l.687.726a.049.049 0 0 1-.015.01.017.017 0 0 1-.006.002l-.005-.002a.049.049 0 0 1-.016-.01l.687-.726Zm-1.332 0a.917.917 0 0 0 0 1.336l.687-.727a.083.083 0 0 1 0 .117l-.687-.726Zm0 1.336 4.016 3.796.687-.726-4.016-3.797-.687.727Zm4.016 3.07-4.016 3.796.687.727 4.016-3.797-.687-.726Zm-4.016 3.796a.917.917 0 0 0 0 1.337l.687-.727a.083.083 0 0 1 0 .117l-.687-.727Zm0 1.337a.978.978 0 0 0 1.332 0l-.687-.727a.049.049 0 0 1 .016-.01.018.018 0 0 1 .005-.002l.006.002c.003 0 .01.004.015.01l-.687.727Zm1.332 0 4.66-4.407-.686-.726-4.66 4.406.686.727Z"
      fill="#0751C2"
    />
  </svg>
);

const StyledArrowNextIcon = styled(ArrowNextIcon)`
  position: absolute;
  z-index: 1;
  top: 50%;
  right: 10px;
  left: unset;
  transform: translateY(-50%);
  cursor: pointer;
  transition: opacity 0.3s ease-in-out;
  display: none;

  ${mediaMMdMin} {
    opacity: 0;
    display: block;
  }
`;

const StyledArrowPrevIcon = styled(StyledArrowNextIcon)`
  left: 10px;
  transform: translateY(-50%) rotate(180deg);
`;
